import type { HelloMessage, RealtimeEnvelope } from '@simulador/realtime-protocol';
import { api } from './api.ts';

export type ConnectionState = 'connecting' | 'live' | 'offline';

export type Subscription = {
  onMessage: (message: RealtimeEnvelope) => void;
  /** Pedir el estado completo por HTTP: al conectar y, sin WebSocket, cada pocos segundos. */
  onSync: () => void;
  onState: (state: ConnectionState) => void;
};

const POLL_MS = 5000;
const MAX_BACKOFF_MS = 15_000;

/** Siguiente espera de reconexión: exponencial con tope y algo de azar. */
export const backoff = (attempt: number, random = Math.random) =>
  Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt) * (0.75 + random() * 0.5);

/**
 * Conecta al WebSocket del evento con un boleto nuevo en cada intento. Mientras no hay conexión,
 * hace polling con onSync. Devuelve la función para desconectarse.
 */
export function subscribe(eventId: string, handlers: Subscription): () => void {
  let stopped = false;
  let ws: WebSocket | null = null;
  let attempt = 0;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let poll: ReturnType<typeof setInterval> | undefined;

  const startPolling = () => {
    if (!poll) poll = setInterval(handlers.onSync, POLL_MS);
  };
  const stopPolling = () => {
    clearInterval(poll);
    poll = undefined;
  };

  const connect = async () => {
    if (stopped) return;
    handlers.onState('connecting');
    try {
      const { url } = await api<{ url: string }>('POST', '/api/realtime/ticket', { eventId });
      if (stopped) return;
      ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${url}`);
      ws.onmessage = (e) => {
        const message = JSON.parse(String(e.data)) as RealtimeEnvelope | HelloMessage;
        if (message.type === 'hello') {
          attempt = 0;
          stopPolling();
          handlers.onState('live');
          handlers.onSync();
          return;
        }
        handlers.onMessage(message);
      };
      ws.onclose = () => scheduleRetry();
    } catch {
      scheduleRetry();
    }
  };

  const scheduleRetry = () => {
    ws = null;
    if (stopped) return;
    handlers.onState('offline');
    startPolling();
    retry = setTimeout(connect, backoff(attempt++));
  };

  void connect();
  return () => {
    stopped = true;
    clearTimeout(retry);
    stopPolling();
    ws?.close();
  };
}
